import { Badge, Flex, Grid, Stack, Text } from '@mantine/core';
import { Appointment, Practitioner } from '@medplum/fhirtypes';
import { formatHumanName, isResource } from '@medplum/core';
import { formatDate } from '@/services/date';
import {
  extractLocationFromAppointment,
  extractPractitionersFromAppointment,
  getAppointmentHostProvider,
} from '@bariendo/lib/scheduling';
import { formatAddress } from '@medplum/core';
import { formatProcedureType } from '@/pages/PatientListPage/services';
import { ORBIT_GRAY } from '@/theme/colors';
import { getOfferingCode, OFFERING_COLORS } from '../PatientTimelineHeader/offeringColors';
import { getRelativeAge } from './shared/relativeAge';

interface PatientProceduresProps {
  appointments: Appointment[];
  loading?: boolean;
}

// Treatment section aligns to the same content-table grid as About above it
// (label column span 4 / value column span 8). The per-offering pill sits in
// the label-column position so the column rhythm holds across sections.
//
// Information hierarchy per procedure (top → bottom of scan-priority):
//   1. Pill (procedure type — primary identifier, color-coded)
//   2. Date (temporal anchor — "is this recent?")
//   3. Provider · Location (operational meta — "who do I follow up with?")
//
// Date is line 1 of the value column because clinicians orient on time first.
// Provider before location because "who" matters more than "where" for follow-up.
const GRID_STYLE = { width: '100%', maxWidth: '100%', overflow: 'hidden' };
const LABEL_COL_STYLE = { display: 'flex', alignItems: 'flex-start' as const, minHeight: '20px' };
const VALUE_STYLE = {
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400 as const,
  color: 'var(--mantine-color-black)',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
};
const META_STYLE = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 400 as const,
  color: ORBIT_GRAY,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
};

function getAppointmentTypeDisplay(appointment: Appointment): string {
  const mapped = formatProcedureType(appointment);
  if (mapped) {
    return mapped;
  }
  const raw = appointment.appointmentType?.text ?? appointment.appointmentType?.coding?.[0]?.display ?? '--';
  return raw.replace(/\s*procedure\s*$/i, '').trim() || raw;
}

function getPhysicianDisplay(appointment: Appointment): string | null {
  const hostProvider = getAppointmentHostProvider(appointment);
  if (hostProvider?.actor) {
    const resource = hostProvider.actor.resource;
    if (resource && isResource(resource, 'Practitioner')) {
      const name = formatHumanName((resource as Practitioner).name?.[0]);
      if (name) return name;
    }
    if (hostProvider.actor.display) return hostProvider.actor.display;
  }
  const practitioners = extractPractitionersFromAppointment(appointment);
  if (practitioners.length === 0) return null;
  const first = practitioners[0];
  const name =
    first && isResource(first, 'Practitioner')
      ? formatHumanName(first.name?.[0])
      : (first as { display?: string }).display;
  return name && name !== '--' ? name : null;
}

function getLocationDisplay(appointment: Appointment): string | null {
  const locations = extractLocationFromAppointment(appointment);
  if (locations.length === 0) return null;
  const value =
    locations
      .map((loc) => {
        if (loc && isResource(loc, 'Location')) {
          return loc.name ?? (loc.address ? formatAddress(loc.address) : undefined);
        }
        return (loc as { display?: string }).display;
      })
      .filter(Boolean)
      .join(', ') || null;
  return value && value !== '--' ? value : null;
}

export function PatientProcedures({ appointments, loading }: PatientProceduresProps) {
  if (loading) {
    return null;
  }
  if (appointments.length === 0) {
    return (
      <Text size="sm" c={ORBIT_GRAY}>
        No appointments found.
      </Text>
    );
  }

  return (
    <Stack gap={12} style={{ paddingTop: 12 }}>
      {appointments.map((appointment) => {
        const offering = getOfferingCode(appointment.appointmentType?.coding?.[0]?.display);
        const fallbackTypeDisplay = getAppointmentTypeDisplay(appointment);
        const dateDisplay = appointment.start ? formatDate(appointment.start, 'MMM D, YYYY') : '--';
        const relativeAge = appointment.start ? getRelativeAge(appointment.start) : null;
        const physician = getPhysicianDisplay(appointment);
        const location = getLocationDisplay(appointment);

        // Provider · Location for line 2. Middle-dot only when both exist.
        const metaParts = [physician, location].filter(Boolean);
        const metaLine = metaParts.length > 0 ? metaParts.join(' · ') : null;

        return (
          <Grid key={appointment.id} align="flex-start" style={GRID_STYLE}>
            <Grid.Col span={3} style={LABEL_COL_STYLE}>
              {offering ? (
                <Badge
                  size="sm"
                  variant="filled"
                  radius="xl"
                  styles={{ root: { backgroundColor: OFFERING_COLORS[offering], color: '#fafcf7' } }}
                >
                  {offering}
                </Badge>
              ) : (
                <Badge size="sm" color="gray" variant="light" radius="xl">
                  {fallbackTypeDisplay}
                </Badge>
              )}
            </Grid.Col>
            <Grid.Col span={9} style={LABEL_COL_STYLE}>
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Flex gap={4} align="baseline" style={{ minWidth: 0 }}>
                  <Text style={VALUE_STYLE}>{dateDisplay}</Text>
                  {relativeAge && <Text style={META_STYLE}>({relativeAge})</Text>}
                </Flex>
                {metaLine && <Text style={META_STYLE}>{metaLine}</Text>}
              </Stack>
            </Grid.Col>
          </Grid>
        );
      })}
    </Stack>
  );
}
